import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { Box, AppBar, Toolbar, Container, Button, Link } from '@mui/material';
import useOffSetTop from 'src/hooks/useOffSetTop';
import useResponsive from 'src/hooks/useResponsive';
import cssStyles from 'src/utils/cssStyles';
import { HEADER } from 'src/config';
import Logo from 'src/components/Logo';
import { PATH_AUTH } from 'src/routes/paths';
import useAuth from 'src/hooks/useAuth';
import useFeatureFlags from 'src/hooks/useFeatureFlags';
import MenuMobile from './MenuMobile';
import menuConfig from './MenuConfig';

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  height: HEADER.MOBILE_HEIGHT,
  transition: theme.transitions.create(['height', 'background-color'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('md')]: {
    height: HEADER.MAIN_DESKTOP_HEIGHT,
  },
}));

const ToolbarShadowStyle = styled('div')(({ theme }) => ({
  left: 0,
  right: 0,
  bottom: 0,
  height: 24,
  zIndex: -1,
  margin: 'auto',
  borderRadius: '50%',
  position: 'absolute',
  width: `calc(100% - 48px)`,
  boxShadow: theme.customShadows.z8,
}));

// eslint-disable-next-line no-unused-vars
const AnimatedLoginButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: 'rgba(40, 60, 39, 0.95) !important',
    transform: 'scale(1.02)',
    transition: 'all 0.2s ease-in-out',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(115, 146, 114, 0.3), transparent)',
    animation: 'shimmer 4s infinite',
    color: 'white',
    zIndex: 1,
  },
  '& .MuiButton-label': {
    position: 'relative',
    zIndex: 2,
  },
  '@keyframes shimmer': {
    '0%': {
      left: '-100%',
    },
    '100%': {
      left: '100%',
    },
  },
}));

export default function MainHeader() {
  const isOffset = useOffSetTop(HEADER.MAIN_DESKTOP_HEIGHT);

  const theme = useTheme();
  const { login } = useAuth();
  const { pathname } = useLocation();
  const featureFlags = useFeatureFlags();
  const isDesktop = useResponsive('up', 'md');

  const isHome = pathname === '/';

  const showRegistration = featureFlags.isFeatureEnabled(featureFlags.featureFlagKeys.registration);

  const onLogin = async () => {
    try {
      await login('', '', 'customer');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppBar sx={{ boxShadow: 0, bgcolor: 'transparent' }}>
      <ToolbarStyle
        disableGutters
        sx={{
          ...(isOffset && {
            ...cssStyles(theme).bgBlur(),
            boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.1)',
            height: { md: HEADER.MAIN_DESKTOP_HEIGHT - 16 },
          }),
        }}
      >
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo sx={{ border: '1px solid rgba(255,255,255,0.1)' }} />
            <Box
              component="img"
              src={
                isOffset
                  ? require('src/assets/LibertasTextGreenLogo.png')
                  : require('src/assets/LibertasTextLogo.png')
              }
              alt="Libertas Text"
              sx={{
                height: '110px',
                display: { xs: 'none', sm: 'block' },
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {showRegistration && (
            <>
              <AnimatedLoginButton
                variant="contained"
                onClick={onLogin}
                sx={{
                  padding: '3px 7px',
                  backgroundColor: 'rgba(56, 80, 55, 0.9)',
                  fontWeight: '500',
                  ...(!isOffset && {
                    border: '1px solid rgba(255,255,255,0.4)',
                  }),
                }}
              >
                Log in
              </AnimatedLoginButton>

              <Link
                variant="subtitle2"
                sx={
                  isOffset
                    ? { pl: theme.spacing(1), color: theme.palette.primary.darker }
                    : {
                        pl: theme.spacing(1),
                        color: theme.palette.common.white,
                      }
                }
                component={RouterLink}
                to={PATH_AUTH.register}
              >
                <b>or Register Now!</b>
              </Link>
              {!isDesktop && (
                <MenuMobile isOffset={isOffset} isHome={isHome} navConfig={menuConfig} />
              )}
            </>
          )}
        </Container>
      </ToolbarStyle>

      {isOffset && <ToolbarShadowStyle />}
    </AppBar>
  );
}
